import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { hasOpenDialogs } from '../../../../../core/functions/dialog-helper';
import { UserRole } from '../../../../../core/enums/users/role';
import { ApplicationStateService } from '../application-state/application-state.service';
import { UserStateService } from '../../../../user/shared/services/user-state/user-state.service';
import { WorkflowState } from '../../../../service-fulfillment/constants/workflow-state';
import {
    getReadOnlyWarningType,
    ReadOnlyWarningContext,
    ReadOnlyWarningSubject,
} from '../../constants/read-only-warning-type-enum';
import {
    READ_ONLY_WARNING_MESSAGES,
    READ_ONLY_WARNING_MODAL_CANCEL_TITLE,
    READ_ONLY_WARNING_MODAL_TITLE,
} from './constants/read-only-warning-modal.constants';

@Injectable({ providedIn: 'root' })
export class ReadOnlyWarningModalService {
    private readonly dialog = inject(MatDialog);
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly userStateService = inject(UserStateService);

    /**
     * Selects and displays the read-only warning for the current subject and user.
     */
    showForReadOnlySubject(subject: ReadOnlyWarningSubject): void {
        const state = this.applicationStateService.getCurrentApplicationState();
        if (!state) {
            throw new Error(
                'ApplicationState has not been initialized. Ensure the application state is set before accessing form controls.',
            );
        }

        const isStaff = this.userStateService.getRole() !== UserRole.Customer;
        const isCancelled = state.workflowState === WorkflowState.Cancelled;
        let context: ReadOnlyWarningContext | undefined;

        if (isCancelled) {
            if (subject === 'application') {
                context = isStaff
                    ? {
                          subject: 'application',
                          role: 'staff',
                          reason: 'cancelled',
                      }
                    : {
                          subject: 'application',
                          role: 'customer',
                          reason: 'cancelled',
                      };
            } else {
                context = isStaff
                    ? {
                          subject: 'frnRequest',
                          role: 'staff',
                          reason: 'cancelled',
                      }
                    : {
                          subject: 'frnRequest',
                          role: 'customer',
                          reason: 'cancelled',
                      };
            }
        } else if (state.handoverToEwa === true && !isStaff) {
            context =
                subject === 'application'
                    ? {
                          subject: 'application',
                          role: 'customer',
                          reason: 'handedOver',
                      }
                    : {
                          subject: 'frnRequest',
                          role: 'customer',
                          reason: 'handedOver',
                      };
        } else if (subject === 'application' && isStaff) {
            context = {
                subject: 'application',
                role: 'staff',
                reason: 'submitted',
            };
        }

        if (!context || hasOpenDialogs(this.dialog)) {
            return;
        }

        const warningType = getReadOnlyWarningType(context);
        const content = READ_ONLY_WARNING_MESSAGES[warningType];

        this.dialog.open(ModalComponent, {
            data: {
                title: READ_ONLY_WARNING_MODAL_TITLE,
                content,
                cancelTitle: READ_ONLY_WARNING_MODAL_CANCEL_TITLE,
            },
            disableClose: false,
        });
    }
}
